import { apiFetch } from "@/services/apiClient";
import cleanMediaTitle from "@/helpers/clean-media-title";
import {
  buildPutioTitleCandidates,
  dedupePutioSearchFiles,
  pickConfidentPutioMatch,
} from "@/helpers/putio-library-match";

// SECURITY: The OAuth token used to live in VITE_PUTIO_OAUTH_TOKEN and
// got inlined into the production bundle. Anyone who could GET the
// SPA's JS could extract a working bearer and impersonate the Put.io
// account. We now route every Put.io API call through the server-side
// reverse proxy at /api/putio, which injects the bearer token from
// the proxy's environment (PUTIO_OAUTH_TOKEN, NO `VITE_` prefix).
//
// The browser never sees the Put.io token. The client.setToken() call
// is gone -- the SDK still attaches an `Authorization: Bearer null`
// header but the proxy strips it and overrides with the real bearer
// server-side.
const CLIENT_ID = import.meta.env.VITE_PUTIO_CLIENT_ID;

// Lazy SDK loader. The @putdotio/api-client bundle (~130KB / 41KB gz) used to be
// STATICALLY imported, which modulepreloaded it on the first paint of EVERY
// route (it sat in index.html). It's now imported DYNAMICALLY on the first
// Put.io API call, so it stays off the critical first-paint fetch. ensureClient()
// builds the singleton exactly once; concurrent callers share the in-flight
// promise. Every method below is wrapped (see the bottom of this file) to await
// ensureClient() before running, so the bodies keep using the module `client`
// reference unchanged — it's guaranteed assigned by the time they execute.
let client = null;
let clientPromise = null;
function ensureClient() {
  if (client) return Promise.resolve(client);
  if (!clientPromise) {
    clientPromise = import("@putdotio/api-client").then((mod) => {
      const PutioAPIClient = mod.default;
      const c = new PutioAPIClient({ clientID: CLIENT_ID });
      // Redirect every axios request from api.put.io/v2 to /api/putio. The path
      // inside the SDK is identical (/files/123, /transfers/list, etc.), just
      // rooted at our server-side proxy instead (which injects the bearer token).
      c.http.defaults.baseURL = "/api/putio";
      client = c;
      return c;
    });
  }
  return clientPromise;
}

// Some indexers expose no magnet — the proxy hands out a tokenized
// /api/torrent/<token> URL that pipes the .torrent from Jackett. For
// several of those indexers Jackett's download endpoint actually
// 302-redirects to a magnet: URI, which Put.io's fetcher won't follow.
// Before adding such a URL as a transfer, ask the proxy to resolve it:
// kind:'magnet' → hand Put.io the real magnet; kind:'torrent' (or any
// failure) → keep the proxy URL, Put.io fetches the bytes as before.
async function resolveProxyTorrentUrl(url) {
  const m =
    typeof url === "string" && url.match(/\/api\/torrent\/([a-f0-9]{32})$/i);
  if (!m) return url;
  try {
    const r = await fetch(`/api/torrent/${m[1]}/resolve`, {
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return url;
    const data = await r.json();
    if (data?.kind === "magnet" && /^magnet:/.test(data.magnet || "")) {
      return data.magnet;
    }
  } catch {
    /* resolve is best-effort — fall back to the proxy URL */
  }
  return url;
}

const methods = {
  // File operations
  async getFile(id) {
    try {
      try {
        const response = await client.get(`/files/${id}`, {
          params: {
            stream_url: true,
            mp4_stream_url: true,
            video_metadata: true,
          },
        });

        return response.data.file;
      } catch (error) {
        console.log("[PUTIO] Direct API failed.", error);
        throw error;
      }
    } catch (error) {
      console.error("Get file error:", error);
      throw error;
    }
  },

  // Lightweight file fetch — the basic file record WITHOUT `stream_url`,
  // `mp4_stream_url`, or `video_metadata`. Requesting those forces Put.io to
  // generate streaming URLs server-side, which measured ~1.4s per call vs
  // ~0.4s for the bare record (proxy-warm: both ~3ms). Use this anywhere the
  // caller only needs metadata for display (name, screenshot, size, type) and
  // is NOT about to play the file — e.g. the Continue Watching rail, which
  // resolves up to 16 cards on home-page mount. Firing the heavy variant there
  // saturated the same-origin connection pool while shelves loaded and stalled
  // navigation to other routes (CLAUDE-TODO: "navigation feels blocked when
  // shelves are still loading"). Playback surfaces (MediaView via the putio
  // store) keep using getFile() so the player still gets stream URLs.
  async getFileLight(id) {
    // No console.error on failure: the only caller (Continue Watching rail)
    // resolves best-effort cards and expects misses — a stale resume entry for
    // a since-deleted Put.io file is normal, not an app error. It catches the
    // throw and drops the card. Surfacing a console error on every home load
    // for a stale entry is just noise.
    const response = await client.get(`/files/${id}`);
    return response.data.file;
  },

  async getFiles(parentId = 0) {
    try {
      const response = await client.Files.Query(parentId);

      return response.data.files;
    } catch (error) {
      console.error("Get files error:", error);
      throw error;
    }
  },

  async getStreamUrl(id) {
    try {
      // The SDK helper string-builds the URL with `https://api.put.io/v2`
      // hardcoded -- it ignores client.http.defaults.baseURL. Rewrite
      // it to our proxy so the browser's <video> element fetches
      // through /api/putio (which injects the bearer server-side)
      // instead of trying to hit api.put.io anonymously.
      const url = client.File.GetHLSStreamURL(id);
      return url.replace("https://api.put.io/v2", "/api/putio");
    } catch (error) {
      console.error("Get video stream URLs error:", error);
      throw error;
    }
  },

  async downloadFile(fileId) {
    try {
      const response = await client.File.Download(fileId);
      return response.data.url;
    } catch (error) {
      console.error("Download file error:", error);
      throw error;
    }
  },

  async getSubtitles(fileId, languages = []) {
    // The SDK's File.Subtitles helper builds a URL with the token in a
    // query param (Put.io's subtitle endpoint takes it that way). Since
    // we no longer expose the token client-side, call the underlying
    // axios path directly through our proxy -- the proxy injects the
    // Authorization header server-side and Put.io accepts that form too.
    try {
      const params = languages?.length ? { language: languages.join(",") } : {};
      const response = await client.http.get(`/files/${fileId}/subtitles`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Get subtitles error:", error);
      throw error;
    }
  },

  async convertToMp4(fileId) {
    try {
      const response = await client.File.ConvertToMp4(fileId);
      return response.data;
    } catch (error) {
      console.error("Convert to MP4 error:", error);
      throw error;
    }
  },

  async getConversionStatus(fileId) {
    try {
      const response = await client.File.ConvertStatus(fileId);
      return response.data;
    } catch (error) {
      console.error("Get conversion status error:", error);
      throw error;
    }
  },

  async deleteFile(fileIds) {
    try {
      const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
      const response = await client.Files.Delete(ids);
      return response.data;
    } catch (error) {
      console.error("Delete file error:", error);
      throw error;
    }
  },

  async renameFile(fileId, name) {
    try {
      const response = await client.File.Rename(fileId, name);
      return response.data;
    } catch (error) {
      console.error("Rename file error:", error);
      throw error;
    }
  },

  async moveFiles(fileIds, parentId) {
    try {
      const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
      const response = await client.Files.Move(ids, parentId);
      return response.data;
    } catch (error) {
      console.error("Move files error:", error);
      throw error;
    }
  },

  async createFolder(name, parentId = 0) {
    try {
      const response = await client.Files.CreateFolder({ name, parentId });
      return response.data;
    } catch (error) {
      console.error("Create folder error:", error);
      throw error;
    }
  },

  async searchFiles(query, fileType = null) {
    try {
      const response = await client.Files.Search(query, {
        perPage: 50,
        fileType,
      });
      return response.data;
    } catch (error) {
      console.error("Search files error:", error);
      throw error;
    }
  },

  // CLAUDE-TODO #383: find an already-downloaded Put.io file/folder that
  // confidently matches a wanted title (movie or single-title TV). Used by the
  // Monitoring view to render an "On Put.io ✓" pill so the user can tell a
  // monitored title is watchable now. Network call lives here; the
  // candidate-building + confident-match scoring is the pure, unit-tested
  // helper in src/helpers/putio-library-match.js.
  //
  // Returns { id, name } of the best match, or null when nothing is confident.
  async findPutioLibraryMatch({
    title,
    year,
    mediaType,
    originalTitle,
    originalName,
  } = {}) {
    const candidates = buildPutioTitleCandidates({
      title,
      originalTitle,
      originalName,
    });
    if (candidates.length === 0) return null;
    let responses;
    try {
      responses = await Promise.allSettled(
        candidates.map((c) => this.searchFiles(c)),
      );
    } catch {
      return null;
    }
    const files = dedupePutioSearchFiles(
      responses.filter((r) => r.status === "fulfilled").map((r) => r.value),
    );
    if (files.length === 0) return null;
    return pickConfidentPutioMatch({
      files,
      wantedTitles: candidates,
      wantedYear: year,
      cleanTitle: cleanMediaTitle,
    });
  },

  async extractFiles(fileIds) {
    try {
      const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
      const response = await client.Files.Extract({ ids });
      return response.data;
    } catch (error) {
      console.error("Extract files error:", error);
      throw error;
    }
  },

  async getStartFrom(fileId) {
    try {
      const response = await client.File.GetStartFrom(fileId);
      return response.data;
    } catch (error) {
      console.error("Get start from error:", error);
      throw error;
    }
  },

  async setStartFrom(fileId, time) {
    try {
      const response = await client.File.SetStartFrom(fileId, time.toString());
      return response.data;
    } catch (error) {
      console.error("Set start from error:", error);
      throw error;
    }
  },

  async findNextVideo(fileId) {
    try {
      const response = await client.File.FindNextVideo(fileId);
      return response.data;
    } catch (error) {
      console.error("Find next video error:", error);
      throw error;
    }
  },

  // Transfer operations
  async getTransfers() {
    try {
      const response = await client.Transfers.Query();
      return response.data.transfers;
    } catch (error) {
      console.error("Get transfers error:", error);
      throw error;
    }
  },

  async getTransfer(id) {
    try {
      const response = await client.Transfers.Get(id);
      return response.data.transfer;
    } catch (error) {
      console.error("Get transfer error:", error);
      throw error;
    }
  },

  async addTransfer(url, parentId = 0) {
    try {
      const response = await client.Transfers.Add({
        url: await resolveProxyTorrentUrl(url),
        save_parent_id: parentId,
      });
      return response.data.transfer;
    } catch (error) {
      console.error("Add transfer error:", error);
      throw error;
    }
  },

  resolveProxyTorrentUrl,

  /**
   * Record a TMDB association for a freshly-added Put.io transfer.
   *
   * Called fire-and-forget right after `addTransfer` resolves, whenever
   * the caller (e.g. /browse/:type/:id, /torbox/play) has TMDB context
   * in hand. Lets the server-side library organizer use the high-
   * confidence TMDB data instead of falling back to a filename parse.
   *
   * Idempotent on the server (UPSERT) — safe to call multiple times.
   * Throws on network/HTTP error so the caller can decide how to
   * surface (current call sites swallow + log; user flow is unblocked).
   *
   * @param {object} args
   * @param {number} args.transferId - Put.io transfer id (from addTransfer).
   * @param {number} args.tmdbId
   * @param {'movie'|'tv'} args.type
   * @param {string} args.title
   * @param {number} [args.year]
   * @returns {Promise<object>} Server response body (e.g. { status: 'recorded' }).
   */
  async associateTransfer({ transferId, tmdbId, type, title, year }) {
    try {
      return await apiFetch("/api/library/associate-transfer", {
        method: "POST",
        json: {
          transfer_id: transferId,
          tmdb_id: tmdbId,
          type,
          title,
          ...(year != null ? { year } : {}),
        },
      });
    } catch (error) {
      console.error("Associate transfer error:", error);
      throw error;
    }
  },

  async addMultipleTransfers(transfers) {
    try {
      const response = await client.Transfers.AddMulti(transfers);
      return response.data;
    } catch (error) {
      console.error("Add multiple transfers error:", error);
      throw error;
    }
  },

  async cancelTransfer(id) {
    // Cancel is occasionally hit by transient 502s — either api.put.io itself
    // hiccuping or the droplet's proxy momentarily failing to reach upstream.
    // One retry with a short backoff is enough to mask the blip; multiple
    // retries would just delay surfacing a real outage.
    const sdkCall = () => client.Transfers.Cancel([id]);
    try {
      const response = await sdkCall();
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 502 || status === 503 || status === 504) {
        // 502/503/504 from put.io or our proxy → transient. Retry once.
        await new Promise((r) => setTimeout(r, 400));
        try {
          const retryResp = await sdkCall();
          return retryResp.data;
        } catch (retryErr) {
          // Annotate with the original status so the UI can show a useful
          // message instead of axios's generic "Request failed".
          retryErr.transientRetryFailed = true;
          retryErr.upstreamStatus = retryErr?.response?.status ?? status;
          console.error("Cancel transfer retry failed:", retryErr);
          throw retryErr;
        }
      }
      console.error("Cancel transfer error:", error);
      throw error;
    }
  },

  async retryTransfer(id) {
    try {
      const response = await client.Transfers.Retry(id);
      return response.data;
    } catch (error) {
      console.error("Retry transfer error:", error);
      throw error;
    }
  },

  async cleanTransfers() {
    try {
      const response = await client.Transfers.ClearAll();
      return response.data;
    } catch (error) {
      console.error("Clean transfers error:", error);
      throw error;
    }
  },

  // Account operations
  async getAccountInfo() {
    try {
      const response = await client.Account.Info();
      return response.data.info;
    } catch (error) {
      console.error("Get account info error:", error);
      throw error;
    }
  },

  async getAccountSettings() {
    try {
      const response = await client.Account.Settings();
      return response.data.settings;
    } catch (error) {
      console.error("Get account settings error:", error);
      throw error;
    }
  },

  // Trash operations
  async emptyTrash() {
    try {
      const response = await client.Trash.Empty();
      return response.data;
    } catch (error) {
      console.error("Empty trash error:", error);
      throw error;
    }
  },

  // Device linking operations
  async linkDevice(code) {
    try {
      const response = await client.Auth.LinkDevice(code);
      return response.data;
    } catch (error) {
      console.error("Link device error:", error);
      throw error;
    }
  },

  // Download operations
  async createDownloadLinks(fileIds, excludeIds = []) {
    try {
      const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
      const response = await client.DownloadLinks.Create({
        ids,
        excludeIds,
      });
      return response.data;
    } catch (error) {
      console.error("Create download links error:", error);
      throw error;
    }
  },

  async getDownloadLinks(downloadLinksId) {
    try {
      const response = await client.DownloadLinks.Get(downloadLinksId);
      return response.data;
    } catch (error) {
      console.error("Get download links error:", error);
      throw error;
    }
  },

  // Zip operations
  async createZip(fileIds, excludeIds = []) {
    try {
      const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
      const response = await client.Zips.Create({
        ids,
        excludeIds,
      });
      return response.data;
    } catch (error) {
      console.error("Create zip error:", error);
      throw error;
    }
  },

  async getZip(zipId) {
    try {
      const response = await client.Zips.Get(zipId);
      return response.data;
    } catch (error) {
      console.error("Get zip error:", error);
      throw error;
    }
  },

  async getZips() {
    try {
      const response = await client.Zips.Query();
      return response.data;
    } catch (error) {
      console.error("Get zips error:", error);
      throw error;
    }
  },

  async retryZip(zipId) {
    try {
      const response = await client.Zips.Retry(zipId);
      return response.data;
    } catch (error) {
      console.error("Retry zip error:", error);
      throw error;
    }
  },

  async cancelZip(zipId) {
    try {
      const response = await client.Zips.Cancel(zipId);
      return response.data;
    } catch (error) {
      console.error("Cancel zip error:", error);
      throw error;
    }
  },
};

// Public surface. Every method is wrapped so the SDK is lazy-loaded (ensureClient)
// before the body runs — the bodies reference the module-level `client`, which is
// assigned by then. Methods are invoked with `this` bound to `methods` so the one
// internal `this.searchFiles(...)` call (in findPutioLibraryMatch) still resolves.
// `client` is exposed as a getter (null until the SDK loads) so existing
// readiness checks (`if (!putio.client) …`) keep working, and `ensureClient` is
// exported for callers that want to warm the SDK explicitly.
const putio = {
  ensureClient,
  get client() {
    return client;
  },
};
for (const name of Object.keys(methods)) {
  const fn = methods[name];
  if (typeof fn !== "function") continue;
  putio[name] = function (...args) {
    return ensureClient().then(() => fn.apply(methods, args));
  };
}

export default putio;
